import { z } from 'zod';

import type {
  FeedProductStatsParams,
  NeedsAttentionCountsParams,
  NeedsAttentionParams,
} from '../services/needs-attention.service';

import { isCapabilityAvailable } from './catalog-capabilities';
import { navContextSearchSchema } from './nav-context';

/** Connectors the needs-attention screen can scope to (the service's connector codes). */
export const NEEDS_ATTENTION_CONNECTORS = ['GOOGLE_MERCHANT', 'META_COMMERCE', 'SHOPIFY'] as const;

/** Page sizes offered on the queue (server max is 100). */
export const NEEDS_ATTENTION_PAGE_SIZES = [20, 50, 100];

/** Rows per page when the URL does not say — 50 so the reason groups cover a useful slice. */
const DEFAULT_PAGE_SIZE = 50;

/**
 * URL search params of /catalog-v2/needs-attention, so filters and page survive Back.
 * `f` holds curation-filter selections by key (multi-select values comma-joined, as
 * the server takes them). Every field is optional and falls back on a bad value.
 */
export const needsAttentionSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  connector: z.enum(NEEDS_ATTENTION_CONNECTORS).optional().catch(undefined),
  label: z.string().optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  size: z.number().int().min(1).max(100).optional().catch(undefined),
  f: z.record(z.string()).optional().catch(undefined),
  /** Image-failures section: its own connector / reason / page, independent of the list above. */
  imgConnector: z.enum(NEEDS_ATTENTION_CONNECTORS).optional().catch(undefined),
  imgReason: z.string().optional().catch(undefined),
  imgPage: z.number().int().min(1).optional().catch(undefined),
  // S8 — where a cross-section link sent the user here (a batch's "N flagged" callout, a
  // product's "needs attention" chip…), so the page can show an origin breadcrumb + back chip.
  ...navContextSearchSchema,
});

/** Parsed needs-attention search params. */
export type NeedsAttentionSearch = z.infer<typeof needsAttentionSearchSchema>;

/** Reason value of the "no label" chip — never sent to the server (it has no unlabelled filter). */
export const NO_LABEL = '__none__';

/**
 * URL search → GET needs-attention params. A reason chip is filtered in SQL (`labelCode`) only
 * while the §50 reads are deployed (an older backend ignores the param); the screen
 * narrows the loaded page client-side either way, which is the whole filter on older deploys.
 */
export function toNeedsAttentionParams(search: NeedsAttentionSearch): NeedsAttentionParams {
  const serverLabel =
    search.label && search.label !== NO_LABEL && isCapabilityAvailable('needsAttentionCounts')
      ? search.label
      : undefined;
  return {
    page: search.page ?? 1,
    size: search.size ?? DEFAULT_PAGE_SIZE,
    ...toNeedsAttentionCountsParams(search),
    ...(serverLabel ? { labelCode: serverLabel } : {}),
  };
}

/** URL search → GET needs-attention/counts params: the list's filters, no paging, no reason. */
export function toNeedsAttentionCountsParams(
  search: NeedsAttentionSearch,
): NeedsAttentionCountsParams {
  return {
    ...(search.q ? { search: search.q } : {}),
    ...(search.connector ? { connectorCode: search.connector } : {}),
    ...(search.f ? { filters: search.f } : {}),
  };
}

/**
 * Finding 7 — URL search → `GET feed-product-stats` params: the same filters as the rows/counts
 * reads, plus the reason chip (B1 — an older deploy just ignores the ones it doesn't know yet).
 */
export function toFeedProductStatsParams(search: NeedsAttentionSearch): FeedProductStatsParams {
  return {
    ...toNeedsAttentionCountsParams(search),
    ...(search.label && search.label !== NO_LABEL ? { labelCode: search.label } : {}),
  };
}
